// `(unnest ds :col …)` / `(unnest ds [:a :b] …)` — flatten nested-
// dataset column(s) into outer rows.
//
// Single-column form:   (unnest ds :col :pick [:a :b] :as [:k :v])
//   Per outer row, the cell is materialised and each inner row emitted
//   as a new output row. `:pick` keeps only the listed inner cols (in
//   inner-schema order). `:as` positionally renames the kept cols.
//
// Multi-column zip form: (unnest ds [:a :b] :pick […] :as [:k :av :bv])
//   The listed columns must share an identical LEADING inner column —
//   the grid key (`bucket`, produced by per-cell `time-bucket`). Per
//   outer row the columns are zip-aligned by that key: the UNION of
//   key values across the listed columns (ascending), with a missing
//   `(column, key)` null-filled. Output is the shared key once, then
//   each column's non-key inner cols in vector order. `:as` (optional)
//   renames positionally over `[key, …col0-nonkey, …col1-nonkey]`; its
//   arity is `1 + Σ(non-key inner cols per column)`.

import { z } from 'zod';

import type { SourcePos } from '../../ast.ts';
import { ShapeError, TypeMismatchError, UnnestableColumnTypeError, UnnestCollisionError } from '../../errors.ts';
import { astShape, nodePos, opCallPos } from '../../normalize.ts';
import { opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { materializedRowCount } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString } from '../../schema/keyword.ts';
import {
  type ColumnType,
  columnTypesEqual,
  describeColumnType,
  type Schema,
  schemaFromEntries,
} from '../../schema/types.ts';
import { type Cell, type Columns, Dataset, type IdentityAxis, MaterializedDataset, type Value } from '../../value.ts';
import { columnNameAstList, columnNameAstName, materializeCell, resolveDsArg } from './helpers.ts';

/**
 * `(unnest ds :col …)` / `(unnest ds [:a :b] …)` — flatten nested-
 * dataset column(s) into outer rows. Two forms:
 *
 *   - **Single column** — `(unnest ds :col :pick [:a :b] :as [:k :v])`.
 *     For each outer row, the cell is materialised and each inner row
 *     emitted as a new output row. `:pick` projects inner cols (inner
 *     order preserved). `:as` positionally renames the kept cols.
 *   - **Multi-column zip** — `(unnest ds [:a :b] :as [:k :av :bv])`.
 *     The listed columns must share an identical LEADING inner column
 *     (the grid key produced by per-cell `time-bucket`). Per outer row
 *     the columns are zip-aligned by that key (union, ascending, missing
 *     `(column, key)` null-filled). `:as` arity is
 *     `1 + Σ(non-key inner cols)`; `:pick` selects each column's
 *     non-key inner cols (the key is always kept).
 *
 * A `LazyDataset` first arg is forced via `ctx.materialize`. Inner
 * cell datasets are materialised via `cell.materialize(ctx)` (L29).
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A listed column isn't on `ds.schema` → `TypeMismatchError`.
 *   - A listed column isn't a `nested-dataset` →
 *     `UnnestableColumnTypeError`.
 *   - A renamed inner col collides with an outer col →
 *     `UnnestCollisionError`.
 *   - Multi-column listed cols don't share a leading inner col →
 *     `ShapeError` with a self-correcting "time-bucket first" hint.
 *
 * The documented raw-datapoints idiom — `(unnest ds :ts-ref :pick
 * [:timestamp :value] :as [:ts :v])` — gets raw per-sample rows from a
 * `CogniteTimeSeries` ref column.
 */
export const unnest = z
  .object({
    name: z.literal('unnest'),
    params: z.tuple([astArg('<dataset>'), z.union([ColumnNameSchema, z.array(ColumnNameSchema).min(1)])]),
    pick: z.array(ColumnNameSchema).optional(),
    as: z.array(ColumnNameSchema).optional(),
  })
  .describe(
    'Flatten nested-dataset column(s) into outer rows. Single column ' +
      '`(unnest ds :col :pick [:a :b])` emits one row per inner row. ' +
      'Multi-column `(unnest ds [:a :b] :as [:k :av :bv])` zip-aligns ' +
      'by the shared leading grid key (the `bucket` produced by a prior ' +
      'per-cell `time-bucket`). `:pick` projects inner cols; `:as` ' +
      'positionally renames them. Composes with threading: ' +
      '`(-> ds (unnest :ts-ref :pick [:timestamp :value]))`.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    docSignature:
      '(unnest ds :refCol [:pick [:cols …]] [:as [:names …]]) — single column; (unnest ds [:refA :refB] :as [:key :aVal :bVal]) — multi-column zip',
    example: { expr: '(-> ds (unnest :ts-ref :pick [:timestamp :value] :as [:ts :v]))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw, colsRaw] = data.params as readonly [Value, ColumnName | readonly ColumnName[]];
      const concrete = await resolveDsArg(dsRaw, '(unnest ds <col(s)> …)', ctx);
      const cols = Array.isArray(colsRaw)
        ? (colsRaw as readonly ColumnName[]).map(columnNameString)
        : [columnNameString(colsRaw as ColumnName)];
      const pickNames = (data.pick ?? []).map((p) => columnNameString(p as ColumnName));
      const asNames = (data.as ?? []).map((a) => columnNameString(a as ColumnName));
      const callPos = opCallPos(ctx.spans, ctx.path);
      const colsPos = nodePos(ctx.spans, paramPath(ctx.path, 1));
      return cols.length === 1
        ? unnestOneColumn(concrete, cols[0] as string, pickNames, asNames, ctx, callPos, colsPos)
        : unnestZip(concrete, cols, pickNames, asNames, ctx, callPos, colsPos);
    },
    dataset: {
      // Single-col form has a fully static schema (inner schema is
      // known from the nested-dataset type). Multi-col zip needs grid-
      // key resolution and inner-schema agreement that depends on
      // runtime validation; return undefined to force-materialise.
      //
      // `referencedColumns` is intentionally omitted (≡ `[]`): unnest's
      // listed cols are op-internal operands, not row-accessor refs;
      // reporting them would erroneously trigger per-cell lift on a
      // nested-dataset col (unnest is itself the fan-out). Apply
      // validates the names at runtime.
      propagateSchema: (data, ctx: PropagateSchemaCtx) => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const colsAst = params[1];
        const colsShape = astShape(colsAst);
        const cols =
          colsShape.kind === 'vector'
            ? columnNameAstList(colsAst)
            : ((): readonly string[] => {
                const nm = columnNameAstName(colsAst);
                return nm === undefined ? [] : [nm];
              })();
        if (cols.length !== 1) {
          return undefined;
        }
        const col = cols[0] as string;
        const colType = ctx.inputSchema.get(col);
        if (colType === undefined || colType.kind !== 'nested-dataset') {
          return undefined;
        }
        const kwargs = data as { readonly pick?: unknown; readonly as?: unknown };
        const pickNames = columnNameAstList(kwargs.pick);
        const asNames = columnNameAstList(kwargs.as);
        const innerColNames = Array.from(colType.innerSchema.keys());
        const picked = pickNames.length === 0 ? innerColNames : innerColNames.filter((n) => pickNames.includes(n));
        return propagateUnnestOneColumn(ctx.inputSchema, col, colType.innerSchema, picked, asNames);
      },
    },
  });

/**
 * Restrict a list of inner columns to the `:pick` selection,
 * preserving inner (schema) order. Identity when `:pick` is absent.
 * Element-shape-agnostic via `nameOf` so it is the single SSOT for
 * BOTH the name-list (single-column) and `[name, type]`-entry (zip)
 * paths and their schema-propagation twins.
 */
const applyPick = <T>(items: readonly T[], pick: readonly string[], nameOf: (item: T) => string): readonly T[] =>
  pick.length === 0 ? items : items.filter((item) => pick.includes(nameOf(item)));

/**
 * Validate every `:pick` name exists in `available`; throws a precise
 * `ShapeError` listing the offenders + the available set otherwise.
 */
const validatePick = (
  pick: readonly string[],
  available: readonly string[],
  label: string,
  callPos: SourcePos,
): void => {
  const set = new Set(available);
  const missing = pick.filter((n) => !set.has(n));
  if (missing.length > 0) {
    throw new ShapeError(
      `:pick names [${missing.join(', ')}] are not ${label}; available: [${available.join(', ')}]`,
      callPos,
      ':pick',
    );
  }
};
/** One emitted output row: every unnest form carries the source outer-row
 * index `ri` so outer survivors and the identity axis project from it. */
type UnnestEmitRow = { readonly ri: number };

/** A payload (non-survivor) output column: its name and a pure projector that
 * yields the cell for a given emitted row. */
type UnnestColumnSpec<RowType extends UnnestEmitRow> = {
  readonly name: string;
  readonly cellFor: (row: RowType) => Cell;
};

/**
 * Column-major assembly shared by both unnest forms. `emitRows` is the single
 * source of the output row set: outer survivors repeat their outer scalar
 * (keyed by `ri`), payload columns project once each, and the identity axis is
 * row-aligned to `emitRows` by construction — so row count and identity length
 * cannot drift. Insertion order is `outerSurvivingCols` then `payloadCols`.
 */
const assembleUnnestOutput = <RowType extends UnnestEmitRow>(
  emitRows: readonly RowType[],
  outerSurvivingCols: readonly string[],
  dsCols: Columns,
  axis: IdentityAxis,
  payloadCols: readonly UnnestColumnSpec<RowType>[],
): { columns: Columns; identity: IdentityAxis } => {
  const columns: Columns = {
    ...Object.fromEntries(
      outerSurvivingCols.map((oc) => [oc, emitRows.map(({ ri }) => (dsCols[oc]?.[ri] ?? null) as Cell)]),
    ),
    ...Object.fromEntries(payloadCols.map(({ name, cellFor }) => [name, emitRows.map(cellFor)])),
  };
  const identity = axis === undefined ? undefined : emitRows.map(({ ri }) => axis[ri]);
  return { columns, identity };
};

const unnestOneColumn = async (
  dataset: MaterializedDataset,
  col: string,
  pickNames: readonly string[],
  asNames: readonly string[],
  ctx: SpecialCtx,
  callPos: SourcePos,
  colsPos: SourcePos,
): Promise<MaterializedDataset> => {
  const colType = dataset.schema.get(col);
  if (colType === undefined) {
    throw new TypeMismatchError(
      `unnest column \`${col}\` to exist on the threaded dataset`,
      'no such column',
      'unnest',
      colsPos,
    );
  }
  if (colType.kind !== 'nested-dataset') {
    throw new UnnestableColumnTypeError(col, describeColumnType(colType), colsPos);
  }

  const innerSchema = colType.innerSchema;
  const allInner = Array.from(innerSchema.keys());
  validatePick(pickNames, allInner, `inner columns of \`${col}\``, callPos);
  // `:pick` keeps a subset of inner columns (inner order preserved).
  const innerColNames = applyPick(allInner, pickNames, (n) => n);
  // `:as` (if supplied) must match the kept inner-column count exactly.
  if (asNames.length > 0 && asNames.length !== innerColNames.length) {
    throw new TypeMismatchError(
      `:as length ${innerColNames.length} — names [${innerColNames.join(' ')}] (${pickNames.length > 0 ? 'picked' : 'inner-schema'} columns)`,
      `received ${asNames.length}`,
      'unnest',
      callPos,
    );
  }
  const renamedInner: readonly string[] = asNames.length > 0 ? asNames : innerColNames;
  const renameMap = new Map(innerColNames.map((orig, i) => [orig, renamedInner[i] as string]));

  // Detect collisions between renamed inner cols and outer cols
  // (excluding the column being unnested, which is being replaced).
  const outerSurvivingCols = Array.from(dataset.schema.keys()).filter((c) => c !== col);
  const outerSet = new Set(outerSurvivingCols);
  const collision = renamedInner.find((renamed) => outerSet.has(renamed));
  if (collision !== undefined) {
    throw new UnnestCollisionError(col, collision, callPos);
  }

  const dsCols = dataset.columns;
  const outerLen = materializedRowCount(dataset);
  // Materialise every per-cell nested Dataset in ONE microtask window
  // (mirrors `liftPerCell`'s `Promise.all`) so the `ts-ref` source
  // fetcher's `queueMicrotask` batcher coalesces all cells into a
  // single CDF round-trip. A serial `for await` here drains the
  // microtask queue between cells, flushing that batcher once per row
  // (one request per row instead of one per unnest). `materializeCell`
  // short-circuits already-materialised cells, so non-lazy inner data
  // stays free.
  const matCells = await Promise.all(
    Array.from({ length: outerLen }, (_, ri) => {
      const cell = dsCols[col]?.[ri];
      return cell instanceof Dataset ? materializeCell(cell, ctx) : Promise.resolve(undefined);
    }),
  );

  // Flatten to the emitted output rows: one per (outer row, inner row),
  // 0 for a null / missing cell. This is the SINGLE source of the output
  // row set — every output column AND the identity axis are projected from
  // it, so the row count and the identity length cannot drift.
  const emitRows = matCells.flatMap((matCell, ri) =>
    matCell === undefined
      ? [] // null / missing cell → no output rows
      : Array.from({ length: materializedRowCount(matCell) }, (_, j) => ({ ri, matCell, j })),
  );

  // Output layout: outer survivors, then the kept (picked) inner cols,
  // renamed — i.e. `[...outerSurvivingCols, ...renamedInner]`. Each survivor
  // repeats its outer scalar; each kept inner col streams from the
  // materialised cell's column arrays.
  const { columns, identity } = assembleUnnestOutput(
    emitRows,
    outerSurvivingCols,
    dsCols,
    dataset.identity,
    innerColNames.map((orig) => ({
      name: renameMap.get(orig) ?? orig,
      cellFor: ({ matCell, j }: (typeof emitRows)[number]) => (matCell.columns[orig]?.[j] ?? null) as Cell,
    })),
  );
  return new MaterializedDataset(
    columns,
    propagateUnnestOneColumn(dataset.schema, col, innerSchema, innerColNames, renamedInner),
    identity,
  );
};

/**
 * Build the post-unnest schema for the single-column form. Apply has
 * already validated that `col` exists and is a `nested-dataset`, so
 * the lookups here are total.
 */
const propagateUnnestOneColumn = (
  inputSchema: Schema,
  col: string,
  innerSchema: Schema,
  innerColNames: readonly string[],
  renamedInner: readonly string[],
): Schema => {
  const outerEntries: readonly (readonly [string, ColumnType])[] = Array.from(inputSchema.entries()).filter(
    ([k]) => k !== col,
  );
  const innerEntries: readonly (readonly [string, ColumnType])[] = innerColNames.map(
    (orig, i) => [renamedInner[i] ?? orig, innerSchema.get(orig) as ColumnType] as const,
  );
  return schemaFromEntries([...outerEntries, ...innerEntries]);
};

/**
 * Resolve the shared leading inner column ("grid key") across the
 * listed columns. Every column must be `nested-dataset` and its inner
 * schema must start with a byte-identical `[name, type]` entry — the
 * `bucket` column produced by per-cell `time-bucket`. Returns the key
 * `[name, type]` plus, per column, its ordered non-key inner entries.
 * Throws the precise `ShapeError` / column-type error on violation.
 */
const resolveZipKey = (
  inputSchema: Schema,
  cols: readonly string[],
  colsPos: SourcePos,
): {
  readonly keyName: string;
  readonly keyType: ColumnType;
  readonly nonKey: readonly (readonly (readonly [string, ColumnType])[])[];
} => {
  type ResolvedCol = {
    readonly key: readonly [string, ColumnType];
    readonly nonKey: readonly (readonly [string, ColumnType])[];
  };
  const resolved: readonly ResolvedCol[] = cols.map((col) => {
    const colType = inputSchema.get(col);
    if (colType === undefined) {
      throw new TypeMismatchError(
        `unnest column \`${col}\` to exist on the threaded dataset`,
        'no such column',
        'unnest',
        colsPos,
      );
    }
    if (colType.kind !== 'nested-dataset') {
      throw new UnnestableColumnTypeError(col, describeColumnType(colType), colsPos);
    }
    const innerEntries = Array.from(colType.innerSchema.entries());
    const lead = innerEntries[0];
    if (lead === undefined) {
      throw new ShapeError(
        `multi-column unnest requires column \`${col}\` to have a leading grid-key inner column`,
        colsPos,
        '#0',
      );
    }
    return { key: lead, nonKey: innerEntries.slice(1) };
  });
  const firstKey = resolved[0]?.key as readonly [string, ColumnType];
  resolved.forEach(({ key }, i) => {
    if (key[0] !== firstKey[0] || !columnTypesEqual(key[1], firstKey[1])) {
      throw new ShapeError(
        `multi-column unnest requires the listed columns to share an identical leading inner column (the time-bucket grid key); \`${cols[i]}\` leads with \`${key[0]}\` but expected \`${firstKey[0]}\`. Multi-column unnest zip-aligns on the shared \`bucket\` key produced by a prior per-cell \`time-bucket\`; to get raw points instead, unnest each column on its own.`,
        colsPos,
        '#0',
      );
    }
  });
  return { keyName: firstKey[0], keyType: firstKey[1], nonKey: resolved.map((r) => r.nonKey) };
};

/** Stable equality/sort key for a grid-key cell value. */
const keyOf = (v: unknown): number | string => (v instanceof Date ? v.getTime() : (v as number | string));

type IndexedCell = {
  readonly byKey: Map<number | string, number>;
  readonly columns: Record<string, readonly Cell[]>;
};

const indexCellByKey = (matCell: MaterializedDataset, keyName: string): IndexedCell => {
  const keyCol = matCell.columns[keyName] ?? [];
  const byKey = new Map<number | string, number>(keyCol.map((kv, i) => [keyOf(kv), i] as const));
  return { byKey, columns: matCell.columns };
};

const unnestZip = async (
  dataset: MaterializedDataset,
  cols: readonly string[],
  pickNames: readonly string[],
  asNames: readonly string[],
  ctx: SpecialCtx,
  callPos: SourcePos,
  colsPos: SourcePos,
): Promise<MaterializedDataset> => {
  const { keyName, keyType, nonKey } = resolveZipKey(dataset.schema, cols, colsPos);

  // `:pick` keeps each column's non-key inner cols (the shared grid key is
  // ALWAYS kept). Validate-then-project in one pass: the pick must name only
  // inner cols that exist in every listed column.
  const effNonKey: readonly (readonly (readonly [string, ColumnType])[])[] = nonKey.map((e, ci) => {
    const entries = e as readonly (readonly [string, ColumnType])[];
    validatePick(
      pickNames,
      entries.map(([n]) => n),
      `non-key inner columns of \`${cols[ci]}\``,
      callPos,
    );
    return applyPick(entries, pickNames, ([n]) => n);
  });

  // Output layout: shared key, then each column's kept non-key inner
  // cols in vector order. `:as` (if any) renames positionally;
  // otherwise the original inner names are kept (collision-checked).
  const baseNames = [keyName, ...effNonKey.flatMap((entries) => entries.map(([n]) => n))];
  if (asNames.length > 0 && asNames.length !== baseNames.length) {
    throw new ShapeError(
      `:as expects ${baseNames.length} names [${baseNames.join(' ')}] (1 shared key + Σ kept non-key inner cols), received ${asNames.length}`,
      callPos,
      ':as',
    );
  }
  const finalNames = asNames.length > 0 ? asNames : baseNames;

  const colSet = new Set(cols);
  const outerSurvivingCols = Array.from(dataset.schema.keys()).filter((c) => !colSet.has(c));
  const outerSet = new Set(outerSurvivingCols);
  finalNames.reduce<Set<string>>((seen, n) => {
    if (outerSet.has(n) || seen.has(n)) {
      throw new UnnestCollisionError(cols.join(','), n, callPos);
    }
    seen.add(n);
    return seen;
  }, new Set<string>());

  const dsCols = dataset.columns;
  const outerLen = materializedRowCount(dataset);

  // Materialise EVERY (row, column) nested cell in ONE microtask
  // window so the `ts-ref` source fetcher's `queueMicrotask` batcher
  // coalesces them into a single CDF round-trip. `materializeCell`
  // short-circuits already-materialised cells.
  const perColByRow = await Promise.all(
    Array.from({ length: outerLen }, (_, ri) =>
      Promise.all(
        cols.map(async (col): Promise<IndexedCell | undefined> => {
          const cell = dsCols[col]?.[ri];
          if (!(cell instanceof Dataset)) {
            return undefined;
          }
          return indexCellByKey(await materializeCell(cell, ctx), keyName);
        }),
      ),
    ),
  );

  // Flatten to the emitted output rows: one per (outer row, distinct grid key),
  // keys ascending. This is the SINGLE source of the output row set — every
  // output column AND the identity axis are projected from it, so the row count
  // and the identity length cannot drift.
  const emitRows = perColByRow.flatMap((perCol, ri) => {
    const unionKeys = perCol.reduce<Map<number | string, unknown>>(
      (acc, ic) => (ic === undefined ? acc : (ic.columns[keyName] ?? []).reduce((m, kv) => m.set(keyOf(kv), kv), acc)),
      new Map<number | string, unknown>(),
    );
    return Array.from(unionKeys.entries())
      .sort(([a], [b]) => {
        if (a < b) {
          return -1;
        }
        return a > b ? 1 : 0;
      })
      .map(([kk, keyValue]) => ({ ri, perCol, kk, keyValue }));
  });

  // Positional non-key output slots: finalNames[s + 1] is inner column `n` of
  // zipped column `ci`, in vector order. Inner names can collide across columns
  // (e.g. two `value`s), so the slot INDEX is the SSOT, never the name.
  const nonKeySlots = effNonKey.flatMap((entries, ci) => entries.map(([n]) => ({ ci, n })));

  // Payload columns in insertion order: the shared key, then the positional
  // non-key slots. Combined with the outer survivors prepended by
  // `assembleUnnestOutput`, the layout is `[...outerSurvivingCols, ...finalNames]`
  // (collision-checked above).
  const { columns, identity } = assembleUnnestOutput(emitRows, outerSurvivingCols, dsCols, dataset.identity, [
    { name: finalNames[0] as string, cellFor: ({ keyValue }: (typeof emitRows)[number]) => (keyValue ?? null) as Cell },
    ...nonKeySlots.map(({ ci, n }, s) => ({
      name: finalNames[s + 1] as string,
      cellFor: ({ perCol, kk }: (typeof emitRows)[number]) => {
        const ic = perCol[ci];
        const idx = ic?.byKey.get(kk);
        return (ic === undefined || idx === undefined ? null : (ic.columns[n]?.[idx] ?? null)) as Cell;
      },
    })),
  ]);
  return new MaterializedDataset(
    columns,
    propagateUnnestZip(dataset.schema, cols, keyName, keyType, effNonKey, asNames),
    identity,
  );
};

/**
 * Build the post-unnest schema for the multi-column zip form. Consumes
 * the `resolveZipKey` result already validated by apply, plus the
 * pick-filtered non-key entries (so the schema and the runtime
 * assembly draw from one source).
 */
const propagateUnnestZip = (
  inputSchema: Schema,
  cols: readonly string[],
  keyName: string,
  keyType: ColumnType,
  effNonKey: readonly (readonly (readonly [string, ColumnType])[])[],
  asNames: readonly string[],
): Schema => {
  const baseEntries: readonly (readonly [string, ColumnType])[] = [
    [keyName, keyType] as const,
    ...effNonKey.flatMap((entries) => entries.map(([n, t]) => [n, t] as const)),
  ];
  const colSet = new Set(cols);
  const outerEntries: readonly (readonly [string, ColumnType])[] = Array.from(inputSchema.entries()).filter(
    ([k]) => !colSet.has(k),
  );
  const finalEntries: readonly (readonly [string, ColumnType])[] = baseEntries.map(
    ([orig, t], i) => [asNames.length > 0 ? (asNames[i] as string) : orig, t] as const,
  );
  return schemaFromEntries([...outerEntries, ...finalEntries]);
};
